import {
  createContactPoint,
  updateContactPoint,
  deleteContactPoint,
} from "../../../../../../../../api/kehrblatt/customer-registry/contact-points"
import {
  createCustomer,
  updateCustomer,
} from "../../../../../../../../api/kehrblatt/customer-registry/customers"
import { createPropertyCustomer } from "../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
import { defaultChannelPriorities } from "../../../../../_logic/channel-priorities"
import {
  enabledChannelSelectionChanged,
  normalizeEnabledChannelsForKind,
} from "../../../../../_logic/contact-channels"
import type { ContactOrderAppointmentRow } from "../../../../../_logic/contact-orders-contract"
import type {
  QuickRecipientContactRow,
  QuickRecipientSavePlan,
  QuickRecipientSaveResult,
} from "../../../_shared/_logic/recipient-quick-edit-contract"

type EditableContactPointKind = "EMAIL" | "MOBILE" | "FIXED_LINE"
type ContactPointChange =
  | { kind: "saved"; contactPoint: ContactPoint }
  | { kind: "deleted"; contactPointId: string }

/** Save explicit contact edits through the registry API; clearing a stored value deletes that contact point. */
export const saveQuickRecipients = async ({
  organizationId,
  appointment,
  workOrder,
  contactPoints,
  plan,
}: {
  organizationId: string
  appointment: ContactOrderAppointmentRow
  workOrder: WorkOrder
  contactPoints: ContactPoint[]
  plan: QuickRecipientSavePlan
}): Promise<QuickRecipientSaveResult> => {
  const linkRoleForAppointment = (): PropertyCustomerRole =>
    appointment.unitScope === "RESIDENTIAL" && workOrder.property_unit_id ? "TENANT" : "OTHER"

  const linkUnitIdForAppointment = () =>
    appointment.unitScope === "RESIDENTIAL" && workOrder.property_unit_id
      ? workOrder.property_unit_id
      : null

  const createOptionalContactPoint = async (
    customer: Customer,
    kind: EditableContactPointKind,
    value: string,
    enabledChannels: ContactChannel[],
  ) => {
    const normalizedValue = value.trim()

    if (!normalizedValue) return null

    const normalizedEnabledChannels = normalizeEnabledChannelsForKind(kind, enabledChannels)
    const payload: ContactPointCreatePayload = {
      kind,
      label: null,
      value: normalizedValue,
      source: "MANUAL",
      customer_id: customer.id,
      organization_id: organizationId,
    }

    if (enabledChannelSelectionChanged(null, kind, normalizedEnabledChannels)) {
      payload.enabled_channels = normalizedEnabledChannels
    }

    return createContactPoint(payload)
  }

  const saveExistingContactPoint = async ({
    row,
    kind,
    contactPointId,
    value,
    enabledChannels,
    originalValue,
  }: {
    row: QuickRecipientContactRow
    kind: EditableContactPointKind
    contactPointId?: string | null
    value: string
    enabledChannels: ContactChannel[]
    originalValue?: string | null
  }): Promise<ContactPointChange | null> => {
    const normalizedValue = value.trim()
    const normalizedOriginalValue = `${originalValue ?? ""}`.trim()
    const normalizedEnabledChannels = normalizeEnabledChannelsForKind(kind, enabledChannels)
    const originalContactPoint = contactPointId
      ? contactPoints.find((contactPoint) => contactPoint.id === contactPointId)
      : null
    const enabledChannelsChanged = enabledChannelSelectionChanged(
      originalContactPoint,
      kind,
      normalizedEnabledChannels,
    )

    if (!row.customerId) return null

    if (!normalizedValue) {
      if (!contactPointId) return null

      await deleteContactPoint(contactPointId)

      return { kind: "deleted", contactPointId }
    }

    if (contactPointId) {
      const payload: ContactPointUpdatePayload = {}

      if (normalizedValue !== normalizedOriginalValue) payload.value = normalizedValue

      if (enabledChannelsChanged) payload.enabled_channels = normalizedEnabledChannels

      if (!Object.keys(payload).length) return null

      return { kind: "saved", contactPoint: await updateContactPoint(contactPointId, payload) }
    }

    if (normalizedValue === normalizedOriginalValue && !enabledChannelsChanged) return null

    const payload: ContactPointCreatePayload = {
      kind,
      label: null,
      value: normalizedValue,
      source: "MANUAL",
      customer_id: row.customerId,
      organization_id: organizationId,
    }

    if (enabledChannelsChanged) payload.enabled_channels = normalizedEnabledChannels

    return { kind: "saved", contactPoint: await createContactPoint(payload) }
  }

  const saveExistingRow = async (row: QuickRecipientContactRow) => {
    const customers: Customer[] = []

    if (row.customerId && row.name !== `${row.originalName ?? ""}`.trim()) {
      customers.push(await updateCustomer(row.customerId, { display_name: row.name }))
    }

    const email = await saveExistingContactPoint({
      row,
      kind: "EMAIL",
      contactPointId: row.emailContactPointId,
      value: row.email,
      enabledChannels: row.emailEnabledChannels,
      originalValue: row.originalEmail,
    })
    const mobile = await saveExistingContactPoint({
      row,
      kind: "MOBILE",
      contactPointId: row.mobileContactPointId,
      value: row.mobile,
      enabledChannels: row.mobileEnabledChannels,
      originalValue: row.originalMobile,
    })
    const fixedLine = await saveExistingContactPoint({
      row,
      kind: "FIXED_LINE",
      contactPointId: row.fixedLineContactPointId,
      value: row.fixedLine,
      enabledChannels: row.fixedLineEnabledChannels,
      originalValue: row.originalFixedLine,
    })
    const changes = [email, mobile, fixedLine]

    return {
      customers,
      contactPoints: changes.flatMap((change) =>
        change?.kind === "saved" ? [change.contactPoint] : [],
      ),
      deletedContactPointIds: changes.flatMap((change) =>
        change?.kind === "deleted" ? [change.contactPointId] : [],
      ),
    }
  }

  const saveNewRow = async (row: QuickRecipientContactRow) => {
    const customer = await createCustomer({
      display_name: row.name,
      type: "PERSON",
      channel_priorities: defaultChannelPriorities(),
      organization_id: organizationId,
    })
    await createPropertyCustomer({
      property_id: workOrder.property_id,
      property_unit_id: linkUnitIdForAppointment(),
      customer_id: customer.id,
      role: linkRoleForAppointment(),
      is_primary: false,
      outreach_enabled: true,
    })
    const contactPoints = (
      await Promise.all([
        createOptionalContactPoint(customer, "EMAIL", row.email, row.emailEnabledChannels),
        createOptionalContactPoint(customer, "MOBILE", row.mobile, row.mobileEnabledChannels),
        createOptionalContactPoint(
          customer,
          "FIXED_LINE",
          row.fixedLine,
          row.fixedLineEnabledChannels,
        ),
      ])
    ).filter(Boolean) as ContactPoint[]

    return { customer, contactPoints }
  }

  const updated = await Promise.all(plan.existingRows.map(saveExistingRow))
  const created = await Promise.all(plan.newRows.map(saveNewRow))

  return {
    customers: [
      ...updated.flatMap((entry) => entry.customers),
      ...created.map((entry) => entry.customer),
    ],
    contactPoints: [
      ...updated.flatMap((entry) => entry.contactPoints),
      ...created.flatMap((entry) => entry.contactPoints),
    ],
    deletedContactPointIds: updated.flatMap((entry) => entry.deletedContactPointIds),
    createdCustomerIds: created.map((entry) => entry.customer.id),
  }
}
